import * as React from "react";
import { Form, Field } from "formik";
import { validateField } from "util/FormsUtils";
import { Button, ButtonToolbar, Col, Input } from "components/bootstrap";
import { FormikFormGroup, InputOptionalInfo as Opt, Select, KeyValueTable, TimezoneSelect } from "components/common";
import { getPathnameWithoutId } from "util/URLUtils";
import useSendTelemetry from "logic/telemetry/useSendTelemetry";
import useLocation from "routing/useLocation";
import { TELEMETRY_EVENT_TYPE } from "telemetry/Constants";
import OidcRoutes from "authentication/components/oidc/Routes";
import getDisplayName from "authentication/helpers/getDisplayName";
import OidcBaseUrlField from "./OidcBaseUrlField";
import payloadFromFormValues from "../helpers/payloadFromFormValues";
import ServerConnectionTest from "../ServerConnectionTest";
const defaultCallbackUrl = `${window.location.origin}${OidcRoutes.AUTH_CALLBACK}`;
export const FORM_VALIDATION = {
  title: { required: true },
  description: {},
  baseUrl: { required: true },
  callbackUrl: { required: true },
  clientId: { required: true },
  clientSecret: { required: true },
  claims: {},
  defaultRoles: { required: true },
  defaultUserTimezone: {}
};
const ServerConfigurationForm = ({
  authenticationBackend,
  values,
  isSubmitting,
  hasSecret,
  isOkta,
  setFieldValue,
  roles,
  validateForm,
  submitAllError,
  goToNext
}) => {
  const { pathname } = useLocation();
  const sendTelemetry = useSendTelemetry();
  const { id } = authenticationBackend;
  const { type } = values;
  const rolesOptions = roles.map((role) => ({ label: role.name, value: role.id }));
  const displayName = getDisplayName(type);
  const oktaBaseUrlHelp = /* @__PURE__ */ React.createElement(React.Fragment, null, "For example ", /* @__PURE__ */ React.createElement("code", null, "https://<your-subdomain>.okta.com"), " or for a custom authorization server:", " ", /* @__PURE__ */ React.createElement("code", null, "https://<yourOktaDomain>/oauth2/<authorizationServerId>"));
  const help = {
    title: void 0,
    description: void 0,
    baseUrl: /* @__PURE__ */ React.createElement("span", null, `The base URL of your ${displayName} environment. `, isOkta && oktaBaseUrlHelp),
    callbackUrl: /* @__PURE__ */ React.createElement("span", null, `The URL ${displayName} will redirect back to after authentication. Most likely`, " it's:", " ", /* @__PURE__ */ React.createElement("code", null, defaultCallbackUrl)),
    claims: void 0,
    clientId: void 0,
    clientSecret: void 0,
    tokenVerifierConnectTimeout: /* @__PURE__ */ React.createElement("span", null, `The time in seconds allowed to validate a token with ${displayName}`),
    defaultRoles: /* @__PURE__ */ React.createElement("span", null, "The default roles synchronized user will obtain. All users need the ", /* @__PURE__ */ React.createElement("code", null, "Reader"), " role, to use the web interface"),
    defaultUserTimezone: /* @__PURE__ */ React.createElement("span", null, "Choose the default time zone for new users.")
  };
  const backendValidationErrors = {
    clientSecret: void 0,
    defaultRoles: void 0
  };
  return /* @__PURE__ */ React.createElement(React.Fragment, null, /* @__PURE__ */ React.createElement(Col, { md: 7 }, /* @__PURE__ */ React.createElement(Form, { className: "form form-horizontal" }, /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.title,
      label: "Title",
      name: "title",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.title),
      placeholder: "Title"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.description,
      label: /* @__PURE__ */ React.createElement(React.Fragment, null, "Description ", /* @__PURE__ */ React.createElement(Opt, null)),
      type: "textarea",
      name: "description",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.description),
      placeholder: "Description"
    }
  ), /* @__PURE__ */ React.createElement(
    OidcBaseUrlField,
    {
      help: help.baseUrl,
      type,
      isOkta,
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.baseUrl)
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.callbackUrl,
      label: "Callback URL",
      name: "callbackUrl",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.callbackUrl),
      placeholder: "Callback URL"
    }
  ), !isOkta && /* @__PURE__ */ React.createElement(Field, { name: "claims" }, ({ meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      help: help.claims,
      error,
      id: "claims",
      label: "Claims",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      KeyValueTable,
      {
        pairs: values.claims,
        editable: !isSubmitting,
        onChange: (claims) => {
          setFieldValue("claims", claims);
        }
      }
    )
  )), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.clientId,
      label: "OAuth Client ID",
      name: "clientId",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.clientId),
      placeholder: "OAuth Client ID"
    }
  ), hasSecret && values.clientSecret === void 0 ? /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "clientSecret",
      label: "OAuth Client Secret",
      labelClassName: "col-sm-3",
      disabled: isSubmitting,
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("clientSecret", "") }, "Reset Secret")
  ) : /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      buttonAfter: hasSecret && values.clientSecret !== void 0 ? /* @__PURE__ */ React.createElement(Button, { type: "button", onClick: () => setFieldValue("clientSecret", void 0) }, "Undo Reset") : void 0,
      help: help.clientSecret,
      label: "OAuth Client Secret",
      name: "clientSecret",
      error: backendValidationErrors == null ? void 0 : backendValidationErrors.clientSecret,
      placeholder: "OAuth Client Secret",
      disabled: isSubmitting,
      validate: validateField(FORM_VALIDATION.clientSecret),
      type: "password"
    }
  ), /* @__PURE__ */ React.createElement(
    FormikFormGroup,
    {
      help: help.tokenVerifierConnectTimeout,
      label: "Token Verifier Connect Timeout",
      type: "number",
      name: "tokenVerifierConnectTimeout",
      disabled: isSubmitting,
      required: true,
      placeholder: "Token Verifier Connect Timeout"
    }
  ), /* @__PURE__ */ React.createElement(Field, { name: "defaultRoles", validate: validateField(FORM_VALIDATION.defaultRoles) }, ({ field: { name, value, onChange, onBlur }, meta: { error } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      bsStyle: error ? "error" : void 0,
      help: help.defaultRoles,
      error: error != null ? error : backendValidationErrors == null ? void 0 : backendValidationErrors.defaultRoles,
      id: "default-roles-select",
      label: "Default Roles",
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      Select,
      {
        multi: true,
        onBlur,
        onChange: (selectedRoles) => onChange({ target: { value: selectedRoles, name } }),
        options: rolesOptions,
        placeholder: "Search for roles",
        value
      }
    )
  )), /* @__PURE__ */ React.createElement(Field, { name: "defaultUserTimezone" }, ({ field: { name, value, onChange } }) => /* @__PURE__ */ React.createElement(
    Input,
    {
      id: "default-user-timezone-select",
      label: "Default User Time Zone",
      help: help.defaultUserTimezone,
      labelClassName: "col-sm-3",
      wrapperClassName: "col-sm-9"
    },
    /* @__PURE__ */ React.createElement(
      TimezoneSelect,
      {
        value: value || "Browser's time zone",
        name: "timezone",
        onChange: (newValue) => onChange({ target: { name, value: newValue } })
      }
    )
  )), submitAllError, /* @__PURE__ */ React.createElement(ButtonToolbar, { className: "pull-right" }, /* @__PURE__ */ React.createElement(
    Button,
    {
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_SERVER_CONFIG_SAVE_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "oidc-server-configuration",
          app_action_value: "server-configuration-step"
        });
      },
      type: "submit"
    },
    "Finish & Save Service"
  ), /* @__PURE__ */ React.createElement(
    Button,
    {
      bsStyle: "primary",
      disabled: isSubmitting,
      onClick: () => {
        sendTelemetry(TELEMETRY_EVENT_TYPE.AUTHENTICATION.OIDC_NEXT_GROUP_SYNC_CLICKED, {
          app_pathname: getPathnameWithoutId(pathname),
          app_section: "oidc-groupsync-step",
          app_action_value: "group-synchronization-button"
        });
        goToNext();
      },
      type: "button"
    },
    "Next: Group Synchronization"
  )))), /* @__PURE__ */ React.createElement(Col, { md: 5 }, /* @__PURE__ */ React.createElement(
    ServerConnectionTest,
    {
      backendId: id,
      prepareSubmitPayload: () => payloadFromFormValues(values),
      validateForm
    }
  )));
};
export default ServerConfigurationForm;
